import { isThisWeek, isToday, isYesterday } from "date-fns"
import { CCUtils } from "../../common/CCUtils"
import {
    activityProgressCollection,
    FBTimestampNow,
    FbUtils,
} from "../../common/FirebaseConfig"
import { ActivityKey, Lessons } from "../../shared/models/ActivityKey"
import {
    AllActivityProgress,
    OverrideKeys,
    Overrides,
} from "../../shared/models/ActivityProgress"
import { Student } from "../../shared/models/Student"
import { ClassStoreSA } from "../classes/ClassStoreSA"
import { log } from "../../common/Logging"

export class ActivityProgressStoreSA {
    private static innerInstance: ActivityProgressStoreSA

    studentActivityProgress?: AllActivityProgress

    static get instance() {
        return this.innerInstance || (this.innerInstance = new this())
    }

    listenForActivityProgress = async (studentId: string) =>
        new Promise<void>((resolve) =>
            activityProgressCollection()
                .doc(studentId)
                .onSnapshot(
                    (activityProgressDoc) => {
                        this.studentActivityProgress = FbUtils.queryDocToDoc(
                            activityProgressDoc,
                        ) as AllActivityProgress
                        resolve()
                    },
                    (err) => {
                        log.debug(err)
                    },
                ),
        )

    updateActivityProgress = (activity: ActivityKey, start?: boolean) => {
        const update = this.studentActivityProgress
        if (update.progress == null) {
            update.progress = []
        }
        let specificActivityProgress = update.progress.find(
            (progress) => progress.activity === activity,
        )
        if (specificActivityProgress == null) {
            specificActivityProgress = {
                activity,
            }
            update.progress.push(specificActivityProgress)
        }
        if (start && specificActivityProgress.start == null) {
            specificActivityProgress.start = FBTimestampNow()
        } else if (!start) {
            specificActivityProgress.finish = FBTimestampNow()
        }
        return activityProgressCollection()
            .doc(this.studentActivityProgress.id)
            .update({
                progress: update.progress,
            })
    }

    activitiesComplete = () => {
        if (
            this.studentActivityProgress == null ||
            this.studentActivityProgress.progress == null
        ) {
            return false
        }
        return (
            this.studentActivityProgress.progress.filter((progress) => {
                return (
                    (progress.activity === ActivityKey.PLAYGROUND ||
                        progress.activity === ActivityKey.SUMMARY_6) &&
                    progress.finish != null
                )
            }).length > 0
        )
    }

    getCurrentGameProgress: () => ActivityKey | undefined = () => {
        if (this.studentActivityProgress?.progress == null) {
            return undefined
        }

        const finishedActivities = this.studentActivityProgress.progress.filter(
            (progress) => progress.finish != null,
        )
        const finishedActivitiesDesc = finishedActivities.sort(
            (a, b) => b.finish.toMillis() - a.finish.toMillis(),
        )

        return finishedActivitiesDesc[0]?.activity
    }

    getNextActivityKey = (activityKey?: ActivityKey | "fireworks") => {
        let nextActivityKey: ActivityKey | "fireworks" = activityKey
        if (nextActivityKey == null) {
            // Get the most recent unfinished activity key
            const currentProgress =
                ActivityProgressStoreSA.instance.getCurrentGameProgress()
            if (currentProgress == null) {
                // None completed, start the first one
                const is5thGrader = ClassStoreSA.instance.is5thGrade()
                if (is5thGrader) {
                    nextActivityKey = ActivityKey.ECIG_101
                } else {
                    nextActivityKey = ActivityKey.ECIG_201
                }
            } else {
                const activityArray = CCUtils.enumValues(
                    ActivityKey,
                ) as ActivityKey[]
                const currentProgressIndex =
                    activityArray.indexOf(currentProgress)
                const currentActivity = activityArray[currentProgressIndex]
                if (
                    currentActivity === ActivityKey.PLAYGROUND ||
                    currentActivity === ActivityKey.SUMMARY_6
                ) {
                    // last activity in list, so fireworks
                    nextActivityKey = "fireworks"
                } else {
                    nextActivityKey = activityArray[currentProgressIndex + 1]
                }
            }
        }
        return nextActivityKey
    }

    hasRestrictions = async (student: Student) => {
        await ActivityProgressStoreSA.instance.listenForActivityProgress(
            student.id,
        )

        const { progress } = this.studentActivityProgress
        const overrides = this.studentActivityProgress.overrides

        const lessonsData = Lessons.map((lesson) => {
            const studentActivityProgresses = lesson.activities.map(
                (activity) => {
                    return progress.find(
                        (progress) => progress.activity === activity,
                    )
                },
            )
            // Uses the last activity in a lesson
            const dateCompleted =
                studentActivityProgresses[studentActivityProgresses.length - 1]
                    ?.finish

            return {
                lesson: `Lesson ${lesson.name}`,
                studentActivityProgresses,
                dateCompleted,
            }
        })

        let restriction:
            | OverrideKeys.CONSECUTIVE_DAYS
            | OverrideKeys.PER_DAY
            | OverrideKeys.PER_WEEK
            | undefined = undefined

        // As a student, I can only do one lesson per day unless my teacher overrides this in the system
        // As a student, I must wait at least one day between lessons, unless my teacher overrides this in the system
        lessonsData.forEach((lessonData) => {
            if (lessonData.dateCompleted) {
                const lessonDate = lessonData.dateCompleted.toDate()
                if (!overrides.PER_DAY && isToday(lessonDate)) {
                    restriction = OverrideKeys.PER_DAY
                }
                if (!overrides.CONSECUTIVE_DAYS && isYesterday(lessonDate)) {
                    restriction = OverrideKeys.CONSECUTIVE_DAYS
                }
            }
        })

        // As a student, I can only do two lessons per week unless my teacher overrides this in the system
        const lessonsThisWeek = lessonsData.filter((lessonData) => {
            const lessonDate = lessonData.dateCompleted?.toDate()
            return lessonDate != null && isThisWeek(lessonDate)
        })

        if (!overrides.PER_WEEK && lessonsThisWeek.length >= 2) {
            restriction = OverrideKeys.PER_WEEK
        }

        return restriction
    }
}
